/**
 * Top-level channel type. The only axis that determines who CAN participate,
 * not who actually does — that's controlled by chat_participant rows.
 *   - dm:               two internal users, 1:1
 *   - channel:          Slack-style internal channel, ≥2 internal users.
 *                       Audience = explicit participants ∪ team gates
 *                       (chat_conversation_team) ∪ role gates
 *                       (chat_conversation_role); visibility private|public.
 *   - customer_support: one portal_user + ≥1 internal rep; optional system/AI
 *   - sms:              one external_sms participant (the customer phone)
 *                       + ≥0 internal reps. Inbound SMS lands here via the
 *                       Twilio webhook → ChatSmsService path; outbound text
 *                       from a rep on this conversation dispatches via
 *                       Twilio rather than MQTT-only fanout.
 */
export enum ChatChannelType {
	Dm = 'dm',
	Channel = 'channel',
	CustomerSupport = 'customer_support',
	Sms = 'sms',
}

export enum ChatConversationStatus {
	Active = 'active',
	Archived = 'archived',
	Resolved = 'resolved',
}

/**
 * Discover-ability of a channel — a binary axis (DMs ignore it; always private).
 *   - private: not discoverable. The audience is explicit participants UNION
 *              the members of any linked team gate (chat_conversation_team)
 *              UNION the holders of any linked role gate (chat_conversation_role).
 *              Gate members auto-join on post.
 *   - public:  any internal user in the tenant can discover (Browse), see, and
 *              post; posting auto-creates a participant row.
 *
 * Team/role gating is a SEPARATE, ADDITIVE concern from this enum — it lives
 * entirely in the chat_conversation_team / chat_conversation_role join tables
 * and applies regardless of private/public, so a private channel can still be
 * gated to whole teams and/or roles. (The former 'team' and 'role' visibility
 * values were removed; existing rows were remapped to 'private' by migration,
 * their gate join rows preserved.)
 */
export enum ChatConversationVisibility {
	Private = 'private',
	Public = 'public',
}

/**
 * Discriminates WHO a participant is. Enforced at MembershipService:
 *   dm:               internal_user only, exactly 2
 *   group:            internal_user only, ≥2
 *   customer_support: exactly 1 portal_user + ≥1 internal_user;
 *                     system and external_* are optional bridges
 */
export enum ChatParticipantType {
	InternalUser = 'internal_user',
	PortalUser = 'portal_user',
	ExternalSms = 'external_sms',
	ExternalEmail = 'external_email',
	System = 'system',
}

export enum ChatParticipantRole {
	Owner = 'owner',
	Admin = 'admin',
	Member = 'member',
	/** For customer_support portal users — implicit in the role type itself. */
	Guest = 'guest',
}

export enum ChatMessageKind {
	Text = 'text',
	File = 'file',
	/** Renders as centered pill in UI: "Alice joined", "Conversation resolved", etc. */
	System = 'system',
	/** Future: call-join/leave events for voice integration. */
	CallEvent = 'call_event',
}

/**
 * Queue-entry lifecycle for customer_support conversations. Distinct from
 * conversation.status because a conversation can be `active` while its
 * queue entry goes through waiting → assigned → resolved. Only
 * customer_support conversations ever have a queue entry.
 */
export enum ChatQueueEntryStatus {
	/** Not yet claimed by a rep. Portal user sees "connecting you…". */
	Waiting = 'waiting',
	/** A rep has claimed/been-assigned this chat. */
	Assigned = 'assigned',
	/** Portal user closed or timed out before a rep picked up. */
	Abandoned = 'abandoned',
	/** Rep resolved the chat. Portal user can no longer resume. */
	Resolved = 'resolved',
}

/**
 * How the queue entry expects to be routed. Drives which rep the
 * RoutingService tries first.
 */
export enum ChatQueueRoutingMode {
	/** Portal user has an assigned rep; try them first, fall back to pool. */
	AssignedRepPreferred = 'assigned_rep_preferred',
	/** No rep preference — go straight to the pool. */
	PoolOnly = 'pool_only',
	/** Rep explicitly requested by the portal user (rare; future use). */
	SpecificRep = 'specific_rep',
}
